import { isStaging } from "@/Configs/config";
import { useTrans } from "@/translations";

import { ComponentProps, FC } from "react";
import { Link } from "react-router-dom";

import { twMerge } from "tailwind-merge";

import { Button } from "./button/index";

interface PageProps {
  title?: string;
  undertext?: string;
  /** Maximum number of columns to show. Columns will collapse on smaller screens. */
  children?: React.ReactNode;
  columns?: 1 | 2;
  fullHeight?: boolean;
  breadcrumbsPaths?: IPageBreadcrumbProps["prevPaths"];
  button?: ComponentProps<typeof Button>;
  link?: {
    label: string;
    uri: string;
    className?: string;
  };
}

export const Page: FC<PageProps> = ({
  title,
  undertext,
  children,
  columns = 1,
  fullHeight,
  breadcrumbsPaths,
  button,
  link,
}) => {
  const { t } = useTrans();

  const renderBreadcrumbs = !!breadcrumbsPaths;

  return (
    <div
      className={twMerge(
        "flex w-full flex-col p-4 pt-5",
        "min-h-[600px]", // Ensure calendars have enough height
        fullHeight ? "h-main-page" : "h-full",
      )}
    >
      <div className="flex items-center justify-between">
        {title && (
          <div>
            <div
              className={twMerge(
                "mt-4 mb-4 flex flex-col-reverse items-baseline lg:flex-row",
                undertext ? "gap-2" : "gap-6",
              )}
            >
              <h1
                className={twMerge(
                  "mb-3 text-3xl sm:text-4xl",
                  isStaging && "text-carrot",
                )}
              >
                {t(title)}
              </h1>

              {undertext && (
                <h2
                  className={twMerge(
                    "text-2xl sm:text-2xl",
                    isStaging && "text-carrot",
                  )}
                >
                  {t(undertext)}
                </h2>
              )}
              {renderBreadcrumbs && (
                <Breadcrumbs
                  prevPaths={breadcrumbsPaths}
                  currentPathName={t(title)}
                />
              )}
            </div>
          </div>
        )}
        <div className="ml-4 flex items-center gap-6">
          {link && (
            <Link
              className={twMerge("text-sm underline", link.className)}
              to={link.uri}
            >
              {t(link?.label)}
            </Link>
          )}
          {button && <Button {...button} />}
        </div>
      </div>
      <div
        className={twMerge(
          "bg-grey1 grid grid-cols-1",
          columns === 2 && "xl:grid-cols-2",
          fullHeight && "h-full grid-rows-1 overflow-hidden",
        )}
      >
        {children}
      </div>
    </div>
  );
};

export interface IPageBreadcrumbProps {
  prevPaths?: {
    url: string;
    name: string;
  }[];
  currentPathName?: string;
}

// TODO: create a navigation map and get breadcrumbs based on that
const Breadcrumbs: FC<IPageBreadcrumbProps> = ({
  prevPaths,
  currentPathName,
}) => {
  return (
    <div className="flex h-9 gap-1">
      {prevPaths?.map(({ name, url }, index) => (
        <div key={`Breadcrumb_${index}`}>
          <Link className="underline" to={url}>
            {name}
          </Link>
          <span className="pl-1">/</span>
        </div>
      ))}
      {!!currentPathName && currentPathName}
    </div>
  );
};
